import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Globe,
  Search,
  FileText,
  Clock,
  RefreshCw,
  Trash2,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  ShoppingCart,
  Download,
} from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LoadingSpinner from "../components/LoadingSpinner";
import { exportAnalysisToPDF } from "../utils/pdfExport";
import { useSubscription } from "../hooks/useSubscription";
import UpgradeModal from "../components/UpgradeModal";

/**
 * CRO Analyzer Page Component
 * Allows users to perform and view CRO (Conversion Rate Optimization) analyses
 */
const CROAnalyzer = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { canCreateAnalyzer, subscription } = useSubscription();

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showNewAnalysisForm, setShowNewAnalysisForm] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [viewingDetails, setViewingDetails] = useState(false);
  const [pollingIntervals, setPollingIntervals] = useState(new Map());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeLimit, setUpgradeLimit] = useState(1);

  // Use ref instead of state to track notified analyses to avoid closure issues
  const notifiedAnalysesRef = useRef(new Set());

  // Form state
  const [formData, setFormData] = useState({
    websiteUrl: "",
    customInstructions: "",
  });
  const [enableCustomCompetitorAnalysis, setEnableCustomCompetitorAnalysis] =
    useState(false);
  const [competitorUrls, setCompetitorUrls] = useState(["", "", ""]);

  /**
   * Fetch user's CRO analyses
   */
  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/cro/analyses");
      setAnalyses(response.data.analyses);

      // Mark already completed/failed analyses as notified to prevent showing toasts on page load
      response.data.analyses.forEach((analysis) => {
        if (analysis.status === "completed" || analysis.status === "failed") {
          notifiedAnalysesRef.current.add(analysis.id);
        }
      });
    } catch (error) {
      console.error("Failed to fetch CRO analyses:", error);
      toast.error("Failed to load CRO analyses");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Start polling for a specific analysis
   */
  const startPolling = (analysisId) => {
    // Don't start if already polling
    if (pollingIntervals.has(analysisId)) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`/api/cro/analyses/${analysisId}`);
        const analysis = response.data.analysis;

        // Update the analysis in the list
        setAnalyses((prev) =>
          prev.map((a) => (a.id === analysisId ? { ...a, ...analysis } : a)),
        );

        // Update selected analysis if it's the one being viewed
        if (selectedAnalysis?.id === analysisId) {
          setSelectedAnalysis((prev) => ({ ...prev, ...analysis }));
        }

        // Stop polling if completed or failed
        if (analysis.status === "completed" || analysis.status === "failed") {
          stopPolling(analysisId);

          // Only show toast if we haven't notified about this analysis yet
          if (!notifiedAnalysesRef.current.has(analysisId)) {
            if (analysis.status === "completed") {
              toast.success("Analysis completed successfully!");
            } else if (analysis.status === "failed") {
              toast.error(
                `Analysis failed: ${analysis.errorMessage || "Unknown error"}`,
              );
            }

            // Mark this analysis as notified
            notifiedAnalysesRef.current.add(analysisId);
          }
        }
      } catch (error) {
        console.error(`Failed to poll analysis ${analysisId}:`, error);
        // Don't stop polling on error, might be temporary
      }
    }, 3000); // Poll every 3 seconds

    setPollingIntervals((prev) => new Map(prev).set(analysisId, intervalId));
  };

  /**
   * Stop polling for a specific analysis
   */
  const stopPolling = (analysisId) => {
    const intervalId = pollingIntervals.get(analysisId);
    if (intervalId) {
      clearInterval(intervalId);
      setPollingIntervals((prev) => {
        const newMap = new Map(prev);
        newMap.delete(analysisId);
        return newMap;
      });
    }
  };

  /**
   * Handle new analysis button click - check limits first
   */
  const handleNewAnalysisClick = async () => {
    const result = await canCreateAnalyzer();
    if (!result.allowed) {
      if (result.reason === "limit_reached") {
        setUpgradeLimit(result.limit);
        setShowUpgradeModal(true);
      } else {
        toast.error("Unable to create new analysis");
      }
      return;
    }
    setShowNewAnalysisForm(true);
  };

  /**
   * Start new CRO analysis (async)
   */
  const handleAnalyze = async (e) => {
    e.preventDefault();

    if (!formData.websiteUrl) {
      toast.error("Please enter a website URL");
      return;
    }

    // Double-check limit before submitting
    const result = await canCreateAnalyzer();
    if (!result.allowed) {
      if (result.reason === "limit_reached") {
        setUpgradeLimit(result.limit);
        setShowUpgradeModal(true);
        setShowNewAnalysisForm(false);
      } else {
        toast.error("Unable to create new analysis");
      }
      return;
    }

    // Validate competitor URLs if enabled
    let validCompetitorUrls = [];
    if (enableCustomCompetitorAnalysis) {
      const filledCompetitorUrls = competitorUrls.filter((url) => url.trim());

      if (filledCompetitorUrls.length === 0) {
        toast.error(
          "Please enter at least one competitor URL or disable custom competitor analysis",
        );
        return;
      }

      validCompetitorUrls = filledCompetitorUrls;
    }

    try {
      setAnalyzing(true);

      const requestData = {
        ...formData,
        competitorUrls: enableCustomCompetitorAnalysis
          ? validCompetitorUrls
          : undefined,
      };

      const response = await axios.post("/api/cro/analyze", requestData);
      const analysis = response.data.analysis;

      toast.success("Analysis started! Processing in background...");

      // Reset form and close
      setFormData({
        websiteUrl: "",
        customInstructions: "",
      });
      setEnableCustomCompetitorAnalysis(false);
      setCompetitorUrls(["", "", ""]);
      setShowNewAnalysisForm(false);

      // Refresh analyses list
      await fetchAnalyses();

      // Start polling for this analysis
      startPolling(analysis.id);
    } catch (error) {
      console.error("CRO analysis error:", error);

      // Check if it's a limit error from backend
      if (error.response?.data?.error?.code === "ANALYZER_LIMIT_REACHED") {
        const limit = error.response.data.error.limit || 1;
        setUpgradeLimit(limit);
        setShowUpgradeModal(true);
        setShowNewAnalysisForm(false);
      } else {
        const errorMessage =
          error.response?.data?.error?.message || "Failed to start analysis";
        toast.error(errorMessage);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  /**
   * View analysis details
   */
  const handleViewAnalysis = async (id) => {
    // Navigate to the analysis URL
    navigate(`/cro-analyzer/${id}`);
  };

  /**
   * Delete analysis
   */
  const handleDeleteAnalysis = async (analysisId, e) => {
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this analysis?")) {
      return;
    }

    try {
      await axios.delete(`/api/cro/analyses/${analysisId}`);
      toast.success("Analysis deleted successfully");
      await fetchAnalyses();

      // If viewing the deleted analysis, close details view
      if (selectedAnalysis?.id === analysisId) {
        setViewingDetails(false);
        setSelectedAnalysis(null);
      }
    } catch (error) {
      console.error("Failed to delete analysis:", error);
      toast.error("Failed to delete analysis");
    }
  };

  useEffect(() => {
    fetchAnalyses();
  }, []);

  // Load analysis from URL parameter
  useEffect(() => {
    const loadAnalysisFromUrl = async () => {
      if (analysisId) {
        try {
          setLoading(true);
          const response = await axios.get(`/api/cro/analyses/${analysisId}`);
          setSelectedAnalysis(response.data.analysis);
          setViewingDetails(true);
        } catch (error) {
          console.error("Failed to fetch analysis details:", error);
          toast.error("Failed to load analysis details");
          // Navigate back to list if analysis not found
          navigate("/cro-analyzer");
        } finally {
          setLoading(false);
        }
      } else {
        // No analysis ID in URL, show list view
        setViewingDetails(false);
        setSelectedAnalysis(null);
      }
    };

    loadAnalysisFromUrl();
  }, [analysisId, navigate]);

  // Start polling for pending/processing jobs on mount
  useEffect(() => {
    analyses.forEach((analysis) => {
      if (analysis.status === "pending" || analysis.status === "processing") {
        startPolling(analysis.id);
      }
    });
  }, [analyses.length]); // Only run when analyses list changes

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervals.forEach((intervalId) => {
        clearInterval(intervalId);
      });
    };
  }, [pollingIntervals]);

  if (viewingDetails && selectedAnalysis) {
    return (
      <AnalysisDetails
        analysis={selectedAnalysis}
        onBack={() => {
          // Navigate back to list view
          navigate("/cro-analyzer");
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="analyzer_limit"
        limit={upgradeLimit}
      />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRO Analyzer</h1>
          <p className="text-gray-600">
            Analyze your eCommerce website and get AI-powered conversion
            optimization recommendations
          </p>
        </div>

        <button
          onClick={handleNewAnalysisClick}
          disabled={analyzing}
          className="btn-primary flex items-center space-x-2"
        >
          <Search className="w-4 h-4" />
          <span>New Analysis</span>
        </button>
      </div>

      {/* New Analysis Form Modal */}
      {showNewAnalysisForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  New CRO Analysis
                </h2>
                <button
                  onClick={() => setShowNewAnalysisForm(false)}
                  disabled={analyzing}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAnalyze} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, websiteUrl: e.target.value })
                    }
                    placeholder="https://example.com"
                    className="input-field w-full"
                    disabled={analyzing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Instructions (Optional)
                  </label>
                  <textarea
                    value={formData.customInstructions}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customInstructions: e.target.value,
                      })
                    }
                    placeholder="Any specific areas you'd like us to focus on?"
                    rows={3}
                    className="input-field w-full"
                    disabled={analyzing}
                  />
                </div>

                {/* Competitor Analysis Checkbox */}
                <div className="pt-2">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableCustomCompetitorAnalysis}
                      onChange={(e) =>
                        setEnableCustomCompetitorAnalysis(e.target.checked)
                      }
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      disabled={analyzing}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable Custom Competitor Analysis
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Get insights on how your site compares to competitors
                  </p>
                </div>

                {/* Competitor URLs Input (shown when checkbox is enabled) */}
                {enableCustomCompetitorAnalysis && (
                  <div className="space-y-3 pt-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Competitor Websites (up to 3)
                    </label>
                    {competitorUrls.map((url, index) => (
                      <div key={index}>
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...competitorUrls];
                            newUrls[index] = e.target.value;
                            setCompetitorUrls(newUrls);
                          }}
                          placeholder={`Competitor ${index + 1} URL (optional)`}
                          className="input-field w-full"
                          disabled={analyzing}
                        />
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">
                      Enter at least one competitor URL to enable this feature
                    </p>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowNewAnalysisForm(false)}
                    disabled={analyzing}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={analyzing}
                    className="btn-primary flex-1 flex items-center justify-center space-x-2"
                  >
                    {analyzing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        <span>Start Analysis</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Analyses List */}
      {loading && analyses.length === 0 ? (
        <LoadingSpinner message="Loading analyses..." />
      ) : analyses.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-8 h-8 text-primary-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No analyses yet
          </h3>
          <p className="text-gray-600 mb-6">
            Start your first CRO analysis to get AI-powered recommendations for
            improving your website's conversion rate
          </p>
          <button
            onClick={handleNewAnalysisClick}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Search className="w-4 h-4" />
            <span>Create First Analysis</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {analyses.map((analysis) => (
            <div
              key={analysis.id}
              onClick={() => handleViewAnalysis(analysis.id)}
              className="card p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Globe className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {analysis.websiteUrl}
                    </h3>
                  </div>

                  <div className="flex items-center space-x-4 ml-8 mt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {new Date(analysis.createdAt).toLocaleDateString()}
                    </div>
                    {analysis.status === "completed" && (
                      <div className="flex items-center text-sm text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Completed
                      </div>
                    )}
                    {analysis.status === "processing" && (
                      <div className="flex items-center text-sm text-blue-600">
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        Processing ({analysis.progress}%)
                      </div>
                    )}
                    {analysis.status === "pending" && (
                      <div className="flex items-center text-sm text-yellow-600">
                        <Clock className="w-4 h-4 mr-1" />
                        Pending
                      </div>
                    )}
                    {analysis.status === "failed" && (
                      <div className="flex items-center text-sm text-red-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Failed
                      </div>
                    )}
                  </div>

                  {/* Progress bar for processing jobs */}
                  {(analysis.status === "pending" ||
                    analysis.status === "processing") && (
                    <div className="ml-8 mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${analysis.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleDeleteAnalysis(analysis.id, e)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Analysis Details Component
 */
const AnalysisDetails = ({ analysis, onBack }) => {
  const navigate = useNavigate();
  const [exportingPDF, setExportingPDF] = useState(false);
  const reportRef = useRef(null);
  const isProcessing =
    analysis.status === "pending" || analysis.status === "processing";
  const isFailed = analysis.status === "failed";
  const isCompleted = analysis.status === "completed";

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      if (!reportRef.current) {
        throw new Error("Report content not found");
      }
      const fileName = await exportAnalysisToPDF(reportRef.current, analysis);
      toast.success(`PDF exported successfully: ${fileName}`);
    } catch (error) {
      console.error("Failed to export PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            CRO Analysis {isProcessing ? "In Progress" : "Results"}
          </h1>
          <p className="text-gray-600">{analysis.websiteUrl}</p>
        </div>
        {/* Status Badge */}
        {analysis.status === "processing" && (
          <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="font-medium">
              Processing ({analysis.progress}%)
            </span>
          </div>
        )}
        {analysis.status === "pending" && (
          <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg">
            <Clock className="w-4 h-4" />
            <span className="font-medium">Pending</span>
          </div>
        )}
        {analysis.status === "completed" && (
          <div className="flex items-center space-x-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg">
            <CheckCircle className="w-4 h-4" />
            <span className="font-medium">Completed</span>
          </div>
        )}
        {analysis.status === "failed" && (
          <div className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">Failed</span>
          </div>
        )}
      </div>

      {/* Processing Progress Bar */}
      {isProcessing && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Analysis in Progress
            </h3>
            <span className="text-sm font-medium text-gray-600">
              {analysis.progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${analysis.progress}%` }}
            ></div>
          </div>

          {/* Analysis Thoughts */}
          {analysis.thoughts && analysis.thoughts.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {analysis.thoughts.slice(-5).map((thought, index) => (
                <div key={index} className="flex items-start space-x-2 text-sm">
                  <span className="text-gray-400 flex-shrink-0">
                    {new Date(thought.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="text-gray-700">{thought.message}</span>
                </div>
              ))}
            </div>
          )}

          <p className="text-sm text-gray-600 mt-3">
            This may take a few minutes. You can leave this page and come back
            later.
          </p>
        </div>
      )}

      {/* Error Message */}
      {isFailed && analysis.errorMessage && (
        <div className="card p-6 bg-red-50 border-red-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Analysis Failed
              </h3>
              <p className="text-sm text-red-700">{analysis.errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Progress Log (for completed analyses) */}
      {(isCompleted || isFailed) &&
        analysis.thoughts &&
        analysis.thoughts.length > 0 && (
          <div className="card p-6">
            <details className="cursor-pointer">
              <summary className="text-sm font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>
                  Analysis Progress Log ({analysis.thoughts.length} events)
                </span>
              </summary>
              <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
                {analysis.thoughts.map((thought, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-2 text-sm py-1"
                  >
                    <span className="text-gray-400 text-xs flex-shrink-0 w-20">
                      {new Date(thought.timestamp).toLocaleTimeString()}
                    </span>
                    <span className="text-gray-700">{thought.message}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

      {/* Platform Detection */}
      {analysis.platform && analysis.platform.name && (
        <div className="card p-6">
          <div className="flex items-start space-x-3">
            <ShoppingCart className="w-5 h-5 text-primary-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                E-commerce Platform Detected
              </h3>
              <div className="flex items-center space-x-4 mb-3">
                <div>
                  <span className="text-2xl font-bold text-primary-600">
                    {analysis.platform.name}
                  </span>
                  {analysis.platform.version && (
                    <span className="text-sm text-gray-500 ml-2">
                      v{analysis.platform.version}
                    </span>
                  )}
                </div>
              </div>
              {analysis.platform.signals &&
                analysis.platform.signals.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                      Detection Signals ({analysis.platform.signals.length})
                    </summary>
                    <ul className="mt-2 ml-4 space-y-1">
                      {analysis.platform.signals.map((signal, index) => (
                        <li key={index} className="text-sm text-gray-600">
                          • {signal}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Google Analytics Detection */}
      {analysis.analytics !== undefined && (
        <div className="card p-6">
          <div className="flex items-start space-x-3">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Google Analytics
              </h3>
              {analysis.analytics && analysis.analytics.length > 0 ? (
                <>
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-lg font-semibold text-green-700">
                        Google Analytics Detected
                      </span>
                    </div>
                    {analysis.analytics[0].trackingId && (
                      <code className="px-2 py-1 bg-gray-100 text-sm text-gray-700 rounded">
                        {analysis.analytics[0].trackingId}
                      </code>
                    )}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                    <p className="text-sm text-blue-900 font-medium mb-2">
                      💡 Unlock Deeper Insights
                    </p>
                    <p className="text-sm text-blue-800 mb-3">
                      Connect your Google Analytics account to access real-time
                      traffic data, conversion metrics, and user behavior
                      analytics.
                    </p>
                    <button
                      onClick={() => navigate("/data-sources")}
                      className="btn btn-primary btn-sm"
                    >
                      Connect Google Analytics →
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    <span className="text-lg font-semibold text-orange-700">
                      Google Analytics Not Detected
                    </span>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-3">
                    <p className="text-sm text-orange-900 font-medium mb-2">
                      ⚠️ Missing Analytics Tracking
                    </p>
                    <p className="text-sm text-orange-800 mb-3">
                      Install Google Analytics to track visitor behavior,
                      conversions, and enable data-driven optimization. It's
                      free and industry-standard.
                    </p>
                    <a
                      href="https://analytics.google.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-orange-700 font-medium hover:text-orange-900 underline"
                    >
                      Learn more about Google Analytics →
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {analysis.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <div className="flex items-center space-x-3 mb-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <h3 className="text-sm font-medium text-gray-600">
                Issues Found
              </h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analysis.summary.issuesCount || 0}
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center space-x-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="text-sm font-medium text-gray-600">
                Recommendations
              </h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analysis.summary.recommendationsCount || 0}
            </p>
          </div>

          <div className="card p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="text-sm font-medium text-gray-600">Analyzed On</h3>
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {new Date(analysis.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Report */}
      {analysis.report && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Analysis Report
              </h2>
            </div>
            <button
              onClick={handleExportPDF}
              disabled={exportingPDF}
              className="btn-secondary flex items-center space-x-2"
            >
              {exportingPDF ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Export to PDF</span>
                </>
              )}
            </button>
          </div>
          <div
            className="prose prose-sm max-w-none bg-gray-50 p-6 rounded-lg overflow-x-auto"
            ref={reportRef}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className="text-2xl font-bold text-gray-900 mt-6 mb-4 border-b-2 border-blue-500 pb-2"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="text-xl font-semibold text-gray-800 mt-5 mb-3"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="text-lg font-medium text-gray-700 mt-4 mb-2"
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className="text-gray-700 mb-3 leading-relaxed"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc list-outside ml-6 mb-3 space-y-1"
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    className="list-decimal list-outside ml-6 mb-3 space-y-1"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li className="text-gray-700 pl-2" {...props} />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto mb-4">
                    <table
                      className="min-w-full border border-gray-300"
                      {...props}
                    />
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <thead className="bg-gray-100" {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="border border-gray-300 px-4 py-2 text-left font-semibold"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td className="border border-gray-300 px-4 py-2" {...props} />
                ),
                code: ({ node, inline, ...props }) =>
                  inline ? (
                    <code
                      className="bg-gray-200 px-1 py-0.5 rounded text-sm"
                      {...props}
                    />
                  ) : (
                    <code
                      className="block bg-gray-200 p-2 rounded text-sm overflow-x-auto"
                      {...props}
                    />
                  ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-3"
                    {...props}
                  />
                ),
                br: () => <br />,
                img: ({ node, ...props }) => (
                  <img
                    className="max-w-full h-auto rounded-lg shadow-md my-4"
                    {...props}
                    alt={props.alt || "Analysis screenshot"}
                  />
                ),
                a: ({ node, ...props }) => {
                  // Check if link is to an image (screenshot)
                  const href = props.href || "";
                  const isImage =
                    href.includes("firebasestorage") ||
                    href.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                  if (isImage) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={href}
                          alt={props.children || "Screenshot"}
                          className="max-w-full h-auto rounded-lg shadow-md my-4 cursor-pointer hover:opacity-90 transition-opacity"
                        />
                      </a>
                    );
                  }

                  return (
                    <a
                      className="text-blue-600 hover:text-blue-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    />
                  );
                },
              }}
            >
              {analysis.report}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {analysis.recommendations && (
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Recommendations
            </h2>
          </div>
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className="text-2xl font-bold text-gray-900 mt-6 mb-4 border-b-2 border-green-500 pb-2"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="text-xl font-bold text-green-600 mt-5 mb-3"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="text-lg font-semibold text-gray-800 mt-4 mb-2"
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className="text-gray-700 mb-3 leading-relaxed"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc list-outside ml-6 mb-4 space-y-1 text-gray-700"
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    className="list-decimal list-outside ml-6 mb-4 space-y-1 text-gray-700"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => <li className="pl-2" {...props} />,
                strong: ({ node, ...props }) => (
                  <strong className="font-bold text-gray-900" {...props} />
                ),
                em: ({ node, ...props }) => (
                  <em className="italic text-gray-700" {...props} />
                ),
                hr: ({ node, ...props }) => (
                  <hr className="border-t-2 border-gray-300 my-6" {...props} />
                ),
                code: ({ node, inline, ...props }) =>
                  inline ? (
                    <code
                      className="bg-gray-200 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800"
                      {...props}
                    />
                  ) : (
                    <code
                      className="block bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm font-mono my-3"
                      {...props}
                    />
                  ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-green-500 pl-4 italic text-gray-600 my-4"
                    {...props}
                  />
                ),
                br: () => <br />,
              }}
            >
              {analysis.recommendations}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default CROAnalyzer;
